import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { z } from 'zod';
import { deliverySchedulesSchema, type DeliverySchedule } from '@tailoring-crm/shared';
import { useApiClient } from '../api/api-context.js';
import type { ApiClient } from '../api/client.js';

/**
 * The shop's delivery speeds, read from and written to the shop settings document.
 *
 * Read through a NARROW schema — the settings endpoint answers with the whole shop profile and
 * this screen wants one list off it. Parsing only what is used means a change to any other
 * section cannot break the booking form's dropdown.
 */

const scheduleListSchema = z.object({
  // Tolerant on read: a settings document written before the field existed simply has none.
  deliverySchedules: deliverySchedulesSchema.default([]),
});

export const deliveryScheduleKeys = {
  all: ['settings', 'delivery-schedules'] as const,
};

export async function getDeliverySchedules(client: ApiClient): Promise<DeliverySchedule[]> {
  const settings = await client.get('/settings', { schema: scheduleListSchema });
  return settings.deliverySchedules ?? [];
}

export async function saveDeliverySchedules(
  client: ApiClient,
  schedules: readonly DeliverySchedule[],
): Promise<DeliverySchedule[]> {
  const settings = await client.patch(
    '/settings',
    { deliverySchedules: schedules },
    { schema: scheduleListSchema },
  );
  return settings.deliverySchedules ?? [];
}

/**
 * Held for five minutes like the stage configuration: a shop changes its delivery speeds about
 * as often as it renames a stage, and the booking screen should not re-ask on every visit.
 */
export function useDeliverySchedules(): UseQueryResult<DeliverySchedule[]> {
  const client = useApiClient();
  return useQuery({
    queryKey: deliveryScheduleKeys.all,
    queryFn: () => getDeliverySchedules(client),
    staleTime: 5 * 60 * 1000,
  });
}

export function useSaveDeliverySchedules() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (schedules: readonly DeliverySchedule[]) =>
      saveDeliverySchedules(client, schedules),
    onSuccess: (saved) => {
      queryClient.setQueryData(deliveryScheduleKeys.all, saved);
    },
  });
}
